import React, { useEffect, useState } from 'react';
import AvatarEditor from 'react-avatar-editor';
import Dropzone from 'react-dropzone';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Media,
  UncontrolledDropdown
} from 'reactstrap';
import Lightbox from 'yet-another-react-lightbox';
import Video from 'yet-another-react-lightbox/dist/plugins/video';
import 'yet-another-react-lightbox/dist/styles.css';

// constants
import LocalStrings from '../../assets/languages';
import appIcons from '../../constants/appIcons';
import { replaceAll } from '../../util/util-common';

// files
import NoImgIcon from '../../assets/img/placeholders/NoImg.svg';

const ImageCropper = props => {
  const {
    displaySize = null,
    displayStyle = null,
    cropperSize,
    requiredSize,
    className,
    insideText,
    editorRef = null,
    cropSaveClicked = null,
    onImageDrop = null,
    bottomButton = false,

    // special params for profile section
    session = null,
    isProfile = false,
    profileframepath = null,
    profilevideopath = null,
    profileFrameVideoHandler = null
  } = props;

  const [canvasResource, setCanvasResource] = useState(props.canvasResource);
  const [imageUrl, setImageUrl] = useState(props.imagepath ?? '');
  const [scale, setScale] = useState(1.2);
  const [imageErrorMsg, setImageErrorMsg] = useState('');
  const [DDToggle, setDDToggle] = useState(false);
  const [open, setOpen] = useState(false);
  const [renderDropzone, setRenderDropzone] = useState(true);
  const [droppedImageDetails, setDroppedImageDetails] = useState({
    name: '',
    size: 0,
    type: ''
  });

  useEffect(() => {
    if (_.isEmpty(imageUrl)) setImageUrl(props.imagepath ?? '');
    if (_.isEmpty(canvasResource))
      setCanvasResource(props.canvasResource ?? '');

    if (isProfile) setRenderDropzone(false);
  }, [props.imagepath, props.canvasResource]);

  let scaleStep = 0.05;
  let defaultScale = 1.2;
  let minScale = 1;
  let maxScale = 2;
  let editor = '';

  const setEditorRef = edit => {
    editor = edit;
    if (editorRef) editorRef(edit);
  };

  const onClickSave = () => {
    if (cropSaveClicked) cropSaveClicked(imageUrl);

    saveImage(null);
  };

  const saveImage = callback => {
    if (editor) {
      let imageType = droppedImageDetails.type;
      let img;
      if (imageType === 'png') img = editor.getImage().toDataURL();
      else img = editor.getImage().toDataURL('image/jpeg', 0.9);

      setImageUrl(img);
      props.onImageSave(img);
      if (callback) {
        setTimeout(() => {
          callback();
        }, 2000);
      }
    }
  };

  const resetImage = () => {
    setCanvasResource('');
    setImageUrl(null);
    setScale(defaultScale);
    setImageErrorMsg('');

    if (isProfile) setRenderDropzone(false);

    props.onImageSave('');
    props.onImageChange(false);
  };

  const handleImageReady = () => {
    if (editor) {
      let imageType = droppedImageDetails?.type;
      let img;
      if (imageType === 'png') img = editor.getImage().toDataURL();
      else img = editor.getImage().toDataURL('image/jpeg', 0.9);
      props.onImageSave(img);
    }
  };

  const onDropFile = files => {
    let file = files[0];
    let requiredWidth = requiredSize.width;
    let requiredHeight = requiredSize.height;
    let reader = new FileReader();
    if (file && file.type.indexOf('image') != -1) {
      setDroppedImageDetails({
        name: file?.name,
        size: file?.size,
        type: file?.type ? _.split(file?.type, '/').pop() : 'jpeg'
      });

      reader.onload = () => {
        let img = new Image();
        img.src = reader.result;
        img.onload = () => {
          let width = img?.width;
          let height = img?.height;

          if (width < requiredWidth || height < requiredHeight) {
            setImageErrorMsg(
              replaceAll(
                LocalStrings.dynamic.kFileMinSizeText,
                ['#width', '#height'],
                [requiredWidth, requiredHeight]
              )
            );
            props.onImageChange(false);
          } else {
            setImageErrorMsg('');
            setCanvasResource(reader.result);
            props.onImageChange(true);

            if (onImageDrop) {
              let fileType = _.split(file.type, '/');
              setTimeout(() => {
                onImageDrop({
                  filename: file.name,
                  type: fileType[0],
                  ext: fileType[1],
                  size: file.size
                });
              }, 1000);
            }
          }
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const onZoomOut = () => {
    let scale1 = scale;
    scale1 = scale1 - scaleStep;
    if (scale1 < minScale) scale1 = minScale;
    setScale(scale1);
  };

  const onZoomIn = () => {
    let scale1 = scale;
    scale1 = scale1 + scaleStep;
    if (scale1 > maxScale) scale1 = maxScale;
    setScale(scale1);
  };

  let hideDropZoneClass = '';
  let hideCropZoneClass = 'd-none';
  let hideControlButton = 'd-none';
  let imageDisplayContainer = !_.isNil(displayStyle)
    ? displayStyle
    : {
        width: displaySize?.width,
        height: displaySize?.height
      };

  if (!_.isEmpty(imageUrl)) {
    hideDropZoneClass = 'd-none';
    hideCropZoneClass = 'd-none';
    hideControlButton = '';
  }
  if (canvasResource && _.isEmpty(imageUrl)) {
    hideDropZoneClass = 'd-none';
    hideCropZoneClass = '';
  }
  if (imageErrorMsg != '') {
    hideControlButton = '';
    hideDropZoneClass = 'd-none';
  }
  if (!renderDropzone) {
    hideDropZoneClass = 'd-none';
    hideControlButton = '';
  }

  return (
    <>
      {/* ----- Image upload dropzone section ----- */}
      <div
        style={imageDisplayContainer}
        className={`image-section ${hideDropZoneClass} aspect-square`}
      >
        <Dropzone onDrop={onDropFile} accept="image/*" multiple={false}>
          {({ getRootProps, getInputProps }) => (
            <div {...getRootProps()} className={`${className}`}>
              <input {...getInputProps()} />

              <div className="custom-text9-normal-primary inner-div">
                <div
                  className="font-icomoon"
                  style={{ rotate: '180deg', fontSize: '1.25rem' }}
                >
                  {appIcons.downloadIcon}
                </div>
                {insideText}
              </div>
            </div>
          )}
        </Dropzone>
      </div>

      {/* ----- Remove button on error - Image preview - Image editor ----- */}
      {!_.isEmpty(imageErrorMsg) ? (
        <div className="d-flex flex-column" style={{ gap: '0.75rem' }}>
          {/* --- Error message --- */}
          <div className={'alert-danger rounded d-flex flex-column p-3'}>
            {imageErrorMsg}
          </div>

          {/* --- Image reset button --- */}
          <div className="">
            <button
              type="button"
              className="custom-button-primary-md"
              onClick={resetImage}
            >
              {LocalStrings.dynamic.kOk}
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* --- Image preview --- */}
          <div
            className={`image-section${!_.isEmpty(hideControlButton) &&
              ` ${hideControlButton}`} position-relative`}
            style={imageDisplayContainer}
          >
            {/* - Image - */}
            <div
              className={`img-responsive position-absolute`}
              style={{
                ...imageDisplayContainer,
                zIndex: 2,
                ...(isProfile
                  ? {
                      backgroundColor: _.isEmpty(imageUrl)
                        ? 'var(--black-color)'
                        : 'transparent',
                      color: 'var(--white-color)',
                      fontSize: '3rem'
                    }
                  : {})
              }}
            >
              {!_.isEmpty(imageUrl) ? (
                <LazyLoadImage
                  className="img-responsive"
                  alt={'this the available gallery item'}
                  effect="blur"
                  placeholder={
                    <img
                      src={NoImgIcon}
                      className="img-responsive"
                      alt="lazy load placeholder on error"
                    />
                  }
                  src={imageUrl}
                  onError={e => {
                    e.target.onerror = null;
                    e.target.src = NoImgIcon;
                  }}
                />
              ) : isProfile ? (
                <>
                  <div className="d-flex justify-content-center align-items-center h-100">
                    {!_.isEmpty(session?.firstname)
                      ? session?.firstname?.[0]?.toUpperCase()
                      : ''}
                    {!_.isEmpty(session?.lastname)
                      ? session?.lastname?.[0]?.toUpperCase()
                      : ''}
                    {!_.isEmpty(session?.firstname) ? '.' : ''}
                  </div>
                </>
              ) : (
                ''
              )}
            </div>

            {/* - Spinner - */}
            <div
              className="attendee-user-img d-flex align-items-center justify-content-center position-absolute"
              style={{
                ...imageDisplayContainer,
                zIndex: 1,
                color: 'var(--theme-color)'
              }}
            >
              <i className={`fa fa-refresh fa-spin`}></i>
            </div>

            {isProfile &&
            !_.isEmpty(profileframepath) &&
            profileframepath !== 'removed' ? (
              <>
                <div
                  className="img-responsive position-absolute"
                  style={{ ...imageDisplayContainer, zIndex: 2 }}
                >
                  <img
                    className="h-100 w-100"
                    src={profileframepath}
                    alt="Profile frame goes here"
                  />
                </div>
              </>
            ) : (
              ''
            )}

            {/* - Remove existing - */}
            {!isProfile && (
              <>
                <div className={`cropper-remove-button-position`}>
                  <button
                    type="button"
                    className={`custom-button-secondary-sm rounded-circle p-2 aspect-square`}
                    onClick={resetImage}
                  >
                    <i className={`fa fa-xmark`} aria-hidden="true"></i>
                  </button>
                </div>
              </>
            )}
          </div>

          {/* --- Image editor --- */}
          <div className={hideCropZoneClass}>
            <div
              className={`d-flex${bottomButton ? ' flex-column' : ''}`}
              style={{ gap: 8 }}
            >
              <AvatarEditor
                image={canvasResource}
                color={[0, 0, 0, 0.5]} // RGBA
                scale={scale}
                rotate={0}
                ref={setEditorRef}
                disabledrop="true"
                width={cropperSize.width}
                height={cropperSize.height}
                className="editor-canvas"
                // style={{ background: '#777' }}
                style={{ background: 'rgba(0, 0, 0, 0.7)' }}
                onImageReady={handleImageReady}
              />

              <div
                className={`d-flex${
                  !bottomButton ? ' flex-column justify-content-between ' : ' '
                }align-items-center`}
                style={
                  !bottomButton
                    ? { height: displaySize?.height ?? 150 }
                    : { gap: '0.5rem' }
                }
              >
                {/* ---  Zoom --- */}
                <div
                  className={`d-flex${!bottomButton ? ' flex-column' : ''}`}
                  style={{ gap: 2 }}
                >
                  <button
                    type="button"
                    className="custom-icon-button-primary-md"
                    style={{
                      borderRadius: bottomButton
                        ? '1rem 0 0 1rem'
                        : '1rem 1rem 0 0'
                    }}
                    onClick={onZoomIn}
                  >
                    <i className="fa fa-search-plus"></i>
                  </button>
                  <button
                    type="button"
                    className="custom-icon-button-primary-md"
                    style={{
                      borderRadius: bottomButton
                        ? '0 1rem 1rem 0'
                        : '0 0 1rem 1rem'
                    }}
                    onClick={onZoomOut}
                  >
                    <i className="fa fa-search-minus"></i>
                  </button>
                </div>

                {/* --- Reset - CropNSave --- */}
                <div
                  className={`d-flex${!bottomButton ? ' flex-column' : ''}`}
                  style={{ gap: 2 }}
                >
                  <button
                    type="button"
                    className="custom-icon-button-danger-md"
                    onClick={resetImage}
                  >
                    <i className={`fa fa-xmark`} aria-hidden="true"></i>
                  </button>

                  <button
                    type="button"
                    className={`custom-icon-button-green-md`}
                    onClick={onClickSave}
                  >
                    <i className={`fa fa-check`} aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ----- Edit my profile section specific extra options dropdown ----- */}
      {isProfile && hideCropZoneClass !== '' && (
        <>
          <UncontrolledDropdown
            className="cropper-remove-button-position"
            direction="right"
          >
            <DropdownToggle
              className="p-0 noBackground"
              onClick={() => setDDToggle(!DDToggle)}
            >
              <Media className="align-items-center">
                {/* ---- user name ---- */}
                <div className="custom-button-secondary-sm rounded-circle p-2 aspect-square">
                  <i className={`fa fa-plus`} aria-hidden="true"></i>
                </div>
              </Media>
            </DropdownToggle>

            <DropdownMenu className="">
              {/* ----- Image upload ----- */}
              {!_.isEmpty(imageUrl) ? (
                <>
                  <DropdownItem
                    className="menu-link mb-1 unselected-menu"
                    style={{ color: 'var(--danger-color)' }}
                    onClick={resetImage}
                  >
                    {LocalStrings.dynamic.kRemoveProfilePic}
                  </DropdownItem>
                </>
              ) : isProfile ? (
                <>
                  <DropdownItem
                    className="menu-link mb-1 unselected-menu"
                    onClick={() => setRenderDropzone(!renderDropzone)}
                  >
                    {!renderDropzone
                      ? LocalStrings.dynamic.kUploadPhoto
                      : LocalStrings.dynamic.kDiscardText}
                  </DropdownItem>
                </>
              ) : (
                ''
              )}

              {/* --- Profile video upload --- */}
              {!_.isEmpty(profilevideopath) &&
              profilevideopath !== 'removed' ? (
                <>
                  <DropdownItem
                    className="menu-link mb-1 unselected-menu"
                    onClick={() => setOpen(true)}
                  >
                    <span>
                      {LocalStrings.dynamic.kViewText}{' '}
                      {LocalStrings.dynamic.kProfileText}{' '}
                      {LocalStrings.dynamic.kVideo}
                    </span>
                  </DropdownItem>
                  <DropdownItem
                    className="menu-link mb-1 unselected-menu"
                    style={{ color: 'var(--danger-color)' }}
                    onClick={() =>
                      profileFrameVideoHandler('profile-video-upload', '')
                    }
                  >
                    {LocalStrings.dynamic.kRemoveProfileVideo}
                  </DropdownItem>
                </>
              ) : (
                <>
                  <DropdownItem
                    className="menu-link mb-1 unselected-menu"
                    onClick={() => profileFrameVideoHandler('profile-video')}
                  >
                    {LocalStrings.dynamic.kUploadVideo}
                  </DropdownItem>
                </>
              )}

              {/* --- Profile frame select --- */}
              {!_.isEmpty(profileframepath) &&
              profileframepath !== 'removed' ? (
                <>
                  <DropdownItem
                    className="menu-link mb-1 unselected-menu"
                    style={{ color: 'var(--danger-color)' }}
                    onClick={() => profileFrameVideoHandler('frame-select', '')}
                  >
                    {LocalStrings.dynamic.kRemoveFrame}
                  </DropdownItem>
                </>
              ) : (
                <>
                  <DropdownItem
                    className="menu-link mb-1 unselected-menu"
                    onClick={() => profileFrameVideoHandler('frame')}
                  >
                    {LocalStrings.dynamic.kAddFrame}
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>

          <Lightbox
            open={open}
            close={() => setOpen(false)}
            plugins={[Video]}
            slides={[
              {
                type: 'video',
                width: 1280,
                height: 720,
                poster: profilevideopath,
                sources: [
                  {
                    src: profilevideopath,
                    type: 'video/mp4'
                  }
                ]
              }
            ]}
            video={{ autoPlay: true }}
          />
        </>
      )}
    </>
  );
};

export default ImageCropper;
